import React, { useMemo } from "react";
import { Box, Chip, Divider, List, ListItem, ListItemText, ListSubheader, Stack, Typography } from "@mui/material";
import { groupByDate, formatDateHeader, relativeLabel } from "./diaryGrouping";
import type { DiaryItem } from "../../models/diaryTypes";

type Props = {
    items: DiaryItem[];
    timeZone?: string; // default "Europe/London"
    onOpenItem: (item: DiaryItem) => void;
    renderIndicators?: (item: DiaryItem) => React.ReactNode;
};

export default function DiaryGroupedList({
    items,
    timeZone = "Europe/London",
    onOpenItem,
    renderIndicators,
}: Props) {
    const groups = useMemo(() => groupByDate(items, timeZone), [items, timeZone]);

    if (groups.length === 0) {
        return <Typography color="text.secondary">No diary items for this period.</Typography>;
    }

    return (
        <List sx={{ width: "100%" }}>
            {groups.map((g) => {
                const rel = relativeLabel(g.dateKey, timeZone);
                const header = formatDateHeader(g.dateKey, timeZone);

                return (
                    <Box key={g.dateKey} sx={{ mb: 1 }}>
                        <ListSubheader
                            sx={{
                                bgcolor: "background.paper",
                                lineHeight: "40px",
                                fontWeight: 700,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                borderRadius: 1,
                            }}
                        >
                            <span>{header}</span>
                            <Stack direction="row" spacing={1} alignItems="center">
                                {rel && <Chip size="small" label={rel} variant="outlined" />}
                                <Chip size="small" label={`${g.items.length}`} variant="outlined" />
                            </Stack>
                        </ListSubheader>

                        <Divider />

                        {g.items.map((item) => (
                            <React.Fragment key={item.id}>
                                <ListItem
                                    onClick={() => onOpenItem(item)}
                                    alignItems="flex-start"
                                >
                                    <ListItemText
                                        primary={
                                            <Stack direction="row" alignItems="center" spacing={1}>
                                                <Typography sx={{ fontWeight: 600 }}>{item.title}</Typography>
                                                {renderIndicators?.(item)}
                                            </Stack>
                                        }
                                        secondary={item.description ?? ""}
                                    />
                                </ListItem>
                                <Divider component="li" />
                            </React.Fragment>
                        ))}
                    </Box>
                );
            })}
        </List>
    );
}
